import {
  Role,
  SubscriptionRenewalBanner,
  rolesManipulator,
  rolesMapping,
  useGetAppSelectorInfo,
  useGetUserInfo,
} from 'auth';
import classNames from 'classnames';
import { useEffect } from 'react';
import { Link, Outlet, matchPath, useLocation, useSearchParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { useCurrentUserQuery } from '@/modules/auth/queries/user.tsx';
import { BUSINESS_QUERY_PARAM } from '@/modules/common/constants/search-params.ts';
import { useGetDefaultBusinessId } from '@/modules/common/hooks/useGetDefaultBusinessId.ts';
import { useSidebarHeaderMenuOptions } from '@/modules/common/hooks/useSidebarHeaderMenuOptions.tsx';
import { useSidebarProfileMenuOptions } from '@/modules/common/hooks/useSidebarProfileMenuOptions.ts';
import { commonPaths } from '@/modules/common/routes.tsx';
import { useSidebarOptions } from '@/modules/employee-dashboard/hooks/useSidebarOptions.tsx';
import { employeeDashboardPaths } from '@/modules/employee-dashboard/routes.tsx';
import { Sidebar, Spinner, TopBar } from 'ui';
import { LogoComplianceWhite } from 'ui/icons/logo';

const EmployeeDashboardWrapper = () => {
  const { avatar } = useGetUserInfo();
  const { data: currentUser, isLoading: isUserLoading } = useCurrentUserQuery();
  const [params, setParams] = useSearchParams();
  const defaultBusinessId = useGetDefaultBusinessId();

  const menuOptions = useSidebarOptions();
  const profileOptions = useSidebarProfileMenuOptions();

  const { pathname } = useLocation();
  const isSingleTaskPageNow = !!matchPath(commonPaths.dynamic.task(), pathname);
  const appSelectorInfo = useGetAppSelectorInfo();
  const { items: headerMenuItems, isLoading: isHeaderMenuItemsLoading } =
    useSidebarHeaderMenuOptions();

  const selectedBusinessId = params.get(BUSINESS_QUERY_PARAM) || undefined;
  const selectedBusiness = (currentUser?.businesses || []).find(
    ({ id }) => id === +(selectedBusinessId || '0'),
  );
  const selectedBusinessMenuItem = headerMenuItems.find(
    ({ value }) => value === selectedBusinessId,
  );

  useEffect(() => {
    if (!selectedBusinessId && defaultBusinessId) {
      params.set(BUSINESS_QUERY_PARAM, defaultBusinessId);
      setParams(params);
    }
  }, [defaultBusinessId, params, selectedBusinessId, setParams]);

  return (
    <div
      className={classNames(
        'bg-gray-900 sm:h-screen w-full flex sm:flex-row flex-col h-[calc(100%-56px)]',
      )}
    >
      {!isSingleTaskPageNow && (
        <Sidebar
          label={
            <Link
              to={{ pathname: employeeDashboardPaths.static.dashboard, search: params.toString() }}
            >
              <img alt="logo" src={LogoComplianceWhite} className="h-8" />
            </Link>
          }
          disabledHeaderMenu={headerMenuItems.length < 2}
          avatarUrl={avatar}
          menuItems={menuOptions}
          profileMenuItems={profileOptions}
          headerMenuItems={headerMenuItems}
          isHeaderMenuItemsLoading={isHeaderMenuItemsLoading}
          headerMenuSelectedItem={selectedBusinessMenuItem}
          user={{ ...currentUser?.user, business: selectedBusiness }}
          compliance={true}
          appSelectorInfo={appSelectorInfo}
          role={
            rolesManipulator?.current_user_role
              ? rolesMapping[rolesManipulator?.current_user_role].name
              : ''
          }
        />
      )}
      <TopBar
        label={
          <Link
            to={{ pathname: employeeDashboardPaths.static.dashboard, search: params.toString() }}
          >
            <img alt="logo" src={LogoComplianceWhite} className="h-8" />
          </Link>
        }
        header="menu"
        avatarUrl={avatar}
        menuItems={menuOptions}
        profileMenuItems={profileOptions}
        headerMenuItems={headerMenuItems}
        headerMenuSelectedValue={selectedBusinessId}
        user={{ ...currentUser?.user, business: selectedBusiness }}
        compliance={true}
        appSelectorInfo={appSelectorInfo}
        role={
          rolesManipulator?.current_user_role
            ? rolesMapping[rolesManipulator?.current_user_role].name
            : ''
        }
      />

      {isUserLoading || !selectedBusiness ? (
        <main className="bg-white flex w-full h-full justify-center items-center">
          <Spinner size="lg" />
        </main>
      ) : (
        <main
          className={classNames(
            'overflow-auto flex flex-col w-full sm:flex-grow-0 flex-grow relative top-0 bottom-0',
            'bg-white items-start justify-center',
            {
              'rounded-tl-xl sm:h-full h-[calc(100%-56px)]': !isSingleTaskPageNow,
              'h-screen': isSingleTaskPageNow,
              'rounded-tr-xl sm:rounded-tr-none sm:rounded-bl-xl rounded-bl-none':
                !isSingleTaskPageNow,
            },
          )}
        >
          <SubscriptionRenewalBanner
            identityCenterApiUrl={import.meta.env.VITE_IDENTITY_CENTER_API_URL}
            selectedApp="compliance"
            storageScope="compliance-employee"
          />
          <Outlet />
        </main>
      )}
    </div>
  );
};

export default withPrivateAccess(EmployeeDashboardWrapper, Role.COMPLIANCE_USER);
